// Overlapping circular avatar pills for a multi-person reference. 22×22px
// circles with two-letter initials, up to 4 visible by default, "+N"
// overflow indicator beyond that. The full attendee list is exposed via
// the parent container's aria-label so screen readers read the cohort
// rather than each individual avatar (which are aria-hidden). Tinted
// backgrounds rotate purely for visual variety — they carry no semantic
// meaning.

import { clsx } from "clsx";

export interface AttendeeStackProps {
  attendees: string[];
  /** Cap on visible avatars. Defaults to 4. Narrower viewports may pass 2 or 3. */
  maxVisible?: number;
}

const TINT_CLASSES = [
  "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
];

const NAME_SUFFIXES = new Set([
  "jr",
  "jr.",
  "sr",
  "sr.",
  "ii",
  "iii",
  "iv",
  "v",
]);

function initialsOf(name: string): string {
  const tokens = name
    .trim()
    .split(/\s+/u)
    .filter((t) => t.length > 0 && !NAME_SUFFIXES.has(t.toLowerCase()));
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase().slice(0, 2);
}

const AVATAR_BASE =
  "inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[10px] font-medium leading-none ring-2 ring-bg-page dark:ring-bg-elev1";

export default function AttendeeStack({
  attendees,
  maxVisible = 4,
}: AttendeeStackProps) {
  if (!attendees || attendees.length === 0) {
    return null;
  }
  const visible = attendees.slice(0, maxVisible);
  const overflow = attendees.length - visible.length;
  const ariaLabel = `Attendees: ${attendees.join(", ")}`;

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className="inline-flex items-center -space-x-1.5"
    >
      {visible.map((name, idx) => (
        <span
          key={`${name}-${idx}`}
          aria-hidden="true"
          className={clsx(AVATAR_BASE, TINT_CLASSES[idx % TINT_CLASSES.length])}
          title={name}
        >
          {initialsOf(name)}
        </span>
      ))}
      {overflow > 0 && (
        <span
          aria-hidden="true"
          className={clsx(
            AVATAR_BASE,
            "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
          )}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}
